import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../../utils/constants/api';
import { ApiResponse } from '../../../types/APIResponse';
import { RootState } from '../../store';
import { addCategory, fetchCategories, updateCategory, deleteCategory } from './categoryAsyncThunk';
import { IAuthResponse } from '../auth/IAuthState';
import { Category } from '../../../types';
import { toast } from 'react-toastify';

export interface DeleteCategory extends IAuthResponse {
    id: string;
}

interface CategoryState {
    data: Category[]; // Array of categories
    status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Fetching status
    loading: boolean; // Create/Update loading
    error: string | null;
}

const initialState: CategoryState = {
    data: [],
    status: 'idle',
    loading: false,
    error: null,
};

// Slice
const categorySlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Categories
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch categories');
                state.error = action.payload || 'Failed to fetch categories';
            });

        // Add Category
        builder
            .addCase(addCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(addCategory.fulfilled, (state, action: PayloadAction<Category>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addCategory.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add category');
                state.error = action.payload || 'Failed to add category';
            });

        // Update Category
        builder
            .addCase(updateCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateCategory.fulfilled, (state, action: PayloadAction<Category>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add category');
                state.error = action.payload || 'Failed to update category';
            });

        // Delete Category
        builder
            .addCase(deleteCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteCategory.fulfilled, (state, action: PayloadAction<DeleteCategory>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                }
                state.loading = false;
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add category');
                state.error = action.payload || 'Failed to delete category';
            });
    },
});

export const selectCategories = (state: RootState) => state.categories;


export default categorySlice.reducer;
